/**
 * Console shell: responsive sidebar + topbar, role-aware navigation, environment badge, win-ID search,
 * keyboard shortcuts and calm/sound preferences (§13.2 applies to admin too). Admin density is higher than the
 * participant app but every control keeps the 44px target (§13.3).
 */
import { isValidWinId, normalizeWinId } from '@windrop/contracts';
import { Button, Modal, Switch, VisuallyHidden, useEnergy } from '@windrop/ui';
import { Keyboard, LogOut, Menu, Search, Settings2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useSession } from '../auth/session';
import { readRuntimeConfig, type AppEnv } from '../lib/runtime-config';
import { NAV_ITEMS, SECTION_LABELS, visibleNav, type NavItem } from './navigation';

const ENV_LABEL: Record<AppEnv, string> = { dev: 'Development', staging: 'Staging', prod: 'Production' };

export function EnvBadge() {
  const { appEnv, buildSha } = readRuntimeConfig();
  return (
    <span
      className="wdc-env"
      data-env={appEnv}
      title={`Build ${buildSha}`}
      aria-label={`${ENV_LABEL[appEnv]} environment, build ${buildSha}`}
    >
      {ENV_LABEL[appEnv]}
    </span>
  );
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !isTypingTarget(e.target)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizeWinId(value.trim());
    if (!normalized || !isValidWinId(normalized)) {
      setError('Enter a win ID such as WIN-XXXXX-XXXXX. Mismatches are reported, never guessed.');
      return;
    }
    setError(null);
    setValue('');
    navigate(`/winners/lookup/${normalized}`);
  };

  return (
    <form
      role="search"
      className="wdc-search"
      onSubmit={submit}
      aria-describedby={error ? `${id}-err` : undefined}
    >
      <label htmlFor={id} className="wd-sr-only">
        Look up a win ID
      </label>
      <Search size={18} aria-hidden="true" />
      <input
        ref={inputRef}
        id={id}
        className="wd-input"
        placeholder="Win ID (press / to focus)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={error ? true : undefined}
      />
      <Button type="submit" variant="secondary">
        Look up
      </Button>
      {error ? (
        <p id={`${id}-err`} role="alert" className="wdc-search__error">
          {error}
        </p>
      ) : null}
    </form>
  );
}

const isTypingTarget = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);

export function ShortcutsHelp({
  open,
  onOpenChange,
  items,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  items: NavItem[];
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Keyboard shortcuts"
      description="Press g, then the letter, to jump."
    >
      <dl className="wdc-shortcuts">
        <div>
          <dt>
            <kbd>/</kbd>
          </dt>
          <dd>Focus the win ID search</dd>
        </div>
        <div>
          <dt>
            <kbd>?</kbd>
          </dt>
          <dd>Open this help</dd>
        </div>
        {items
          .filter((i) => i.shortcut)
          .map((i) => (
            <div key={i.id}>
              <dt>
                <kbd>g</kbd> <kbd>{i.shortcut}</kbd>
              </dt>
              <dd>{i.label}</dd>
            </div>
          ))}
      </dl>
    </Modal>
  );
}

export function PreferencesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const energy = useEnergy();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Display preferences"
      description="These follow you on this device only."
    >
      <div className="wd-stack">
        <Switch
          label="Calm mode (reduced motion, no confetti)"
          checked={energy.userPreference === 'CALM' || (energy.userPreference === 'SYSTEM' && energy.calm)}
          onCheckedChange={(on) => energy.setUserPreference(on ? 'CALM' : 'FULL')}
        />
        <Switch
          label="Sound effects"
          checked={energy.soundEnabled}
          onCheckedChange={energy.setSoundEnabled}
        />
        <p className="wd-muted">
          Current motion level: <strong>{energy.energy === 'CALM' ? 'Calm' : 'Full'}</strong>
          {energy.prefersReducedMotion ? ' (your system asks for reduced motion)' : ''}.
        </p>
      </div>
    </Modal>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { session, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const items = visibleNav(session);
  const { webOrigin } = readRuntimeConfig();

  // Close the drawer on navigation (mobile).
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // `?` → help, `g` + letter → navigate.
  useEffect(() => {
    let pendingG = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (pendingG) {
        pendingG = false;
        const item = items.find((i) => i.shortcut === e.key.toLowerCase());
        if (item) {
          e.preventDefault();
          navigate(item.to);
        }
        return;
      }
      if (e.key === 'g') {
        pendingG = true;
        clearTimeout(timer);
        timer = setTimeout(() => {
          pendingG = false;
        }, 1500);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(timer);
    };
  }, [items, navigate]);

  const sections = (['operate', 'catalogue', 'insight', 'admin'] as const).filter((s) =>
    items.some((i) => i.section === s),
  );

  return (
    <div className="wdc-shell" data-nav-open={navOpen ? 'true' : undefined}>
      <a href="#main" className="wdc-skip">
        Skip to main content
      </a>
      <header className="wdc-topbar">
        <button
          type="button"
          className="wd-btn wd-touch wdc-topbar__menu"
          data-variant="quiet"
          aria-expanded={navOpen}
          aria-controls="console-nav"
          onClick={() => setNavOpen((o) => !o)}
        >
          {navOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          <VisuallyHidden>{navOpen ? 'Close navigation' : 'Open navigation'}</VisuallyHidden>
        </button>
        <div className="wdc-brand">
          <span className="wdc-brand__mark" aria-hidden="true">
            W
          </span>
          <span className="wdc-brand__name">
            <strong>{session?.tenantSlug ?? 'Windrop'}</strong>
            <span className="wd-muted"> console</span>
          </span>
          <EnvBadge />
        </div>
        {session && items.some((i) => i.id === 'winners') ? (
          <GlobalSearch />
        ) : (
          <span className="wdc-topbar__spacer" />
        )}
        <div className="wdc-topbar__actions">
          <Button
            variant="quiet"
            onClick={() => setHelpOpen(true)}
            icon={<Keyboard size={18} />}
            aria-keyshortcuts="?"
          >
            Shortcuts
          </Button>
          <Button variant="quiet" onClick={() => setPrefsOpen(true)} icon={<Settings2 size={18} />}>
            Display
          </Button>
          <span className="wdc-user" title={session?.email}>
            {session?.name}
            <span className="wd-muted"> · {session?.roles.map(humanizeRole).join(', ')}</span>
          </span>
          <Button
            variant="quiet"
            icon={<LogOut size={18} />}
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            Sign out
          </Button>
        </div>
      </header>
      <nav id="console-nav" className="wdc-sidebar" aria-label="Console">
        {sections.map((section) => (
          <div key={section} className="wdc-sidebar__section">
            <h2 className="wdc-sidebar__heading">{SECTION_LABELS[section]}</h2>
            <ul>
              {items
                .filter((i) => i.section === section)
                .map((item) => (
                  <li key={item.id}>
                    <NavLink to={item.to} end={item.to === '/'} className="wdc-navlink">
                      {item.label}
                    </NavLink>
                  </li>
                ))}
            </ul>
          </div>
        ))}
        {session?.tenantSlug ? (
          <p className="wdc-sidebar__foot wd-muted">
            <a href={`${webOrigin}/`} target="_blank" rel="noreferrer">
              Open participant site
            </a>
          </p>
        ) : null}
      </nav>
      <main id="main" className="wdc-main" tabIndex={-1}>
        {children ?? <Outlet />}
      </main>
      <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} items={items} />
      <PreferencesDialog open={prefsOpen} onOpenChange={setPrefsOpen} />
    </div>
  );
}

export const humanizeRole = (role: string): string =>
  role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export { NAV_ITEMS };
